import { createContext, useContext, ReactNode, useState, useRef } from "react";
import { useForm, UseFormReturn } from "react-hook-form";
import { DynamicFieldItem } from "../../../../model/dynamic-forms";
import { Money } from "../../../../model/common/money";
import { SectionDefinition } from "../../../../model/service-request/section-definition";

export type SectionFormData = Record<string, DynamicFieldItem[]>;

export interface ServiceRequestFormData {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    homeAddress: string;
    meterType: "prepaid" | "postpaid" | "";
    meterNumber: string;
    meterVerified: boolean;
    requestTypeId: string;
    requestTypeName: string;
    requestTypeDefinitionId: string;
    sections: SectionFormData;
    amount: Money | null;
    paymentMethod: "pay_now" | "pay_with_link" | null;
    payOnSubmit: boolean | null;
}

const stepFields: Record<number, (keyof ServiceRequestFormData)[]> = {
    0: ["firstName", "lastName", "email", "phone", "homeAddress", "meterType", "meterNumber", "meterVerified"],
    1: ["requestTypeId"],
};

interface ServiceRequestFormContextValue {
    form: UseFormReturn<ServiceRequestFormData>;
    validateStep: (stepIndex: number) => Promise<boolean>;
    setSectionDefinitions: (defs: SectionDefinition[]) => void;
    forceValidateSections: boolean;
    setForceValidateSections: (value: boolean) => void;
}

const ServiceRequestFormContext = createContext<ServiceRequestFormContextValue | null>(null);

/**
 * "Channel of Complaint" is stamped server-side from the submitting portal
 * (Admin Web App here) — the agent must not pick it, so the field is hidden
 * from the form and excluded from validation.
 */
export const CHANNEL_OF_COMPLAINT_ATTRIBUTE_ID = "e0000000-0000-0000-0000-000000000206";

export const isServerStampedAttribute = (attributeId?: string): boolean =>
    attributeId?.toLowerCase() === CHANNEL_OF_COMPLAINT_ATTRIBUTE_ID;

/**
 * The submit command validates the payload against the CUSTOMER-visible
 * definition (hidden attributes and the sections they empty out are removed
 * server-side, their values stamped from CustomerDetails). The agent form must
 * therefore SUBMIT exactly the customer-visible field set.
 */
export const isAgentVisibleAttribute = (a: { id?: string; isEnabled?: boolean; isHidden?: boolean }): boolean =>
    a.isEnabled !== false && a.isHidden !== true && !isServerStampedAttribute(a.id);

/**
 * Hidden (but enabled) attributes — e.g. Account No, Metered — are still
 * RENDERED in the agent form, prefilled and read-only for the agent's
 * reference; they are only excluded from the submitted payload (see
 * isAgentVisibleAttribute). Disabled attributes and the server-stamped
 * channel attribute are never rendered.
 */
export const isAgentRenderableAttribute = (a: { id?: string; isEnabled?: boolean }): boolean =>
    a.isEnabled !== false && !isServerStampedAttribute(a.id);

const isSectionValid = (section: SectionDefinition, storedValues: DynamicFieldItem[]): boolean => {
    const required = section.attributeDefinitions.filter((a) => a.isRequired && isAgentVisibleAttribute(a));
    return required.every((attr) => {
        const val = storedValues.find((v) => v.fieldId === attr.id);
        return val && val.fieldValue !== "" && val.fieldValue !== null && val.fieldValue !== undefined;
    });
};

export const ServiceRequestFormProvider = ({ children }: { children: ReactNode }) => {
    const form = useForm<ServiceRequestFormData>({
        defaultValues: {
            firstName: "",
            lastName: "",
            email: "",
            phone: "",
            homeAddress: "",
            meterType: "",
            meterNumber: "",
            meterVerified: false,
            requestTypeId: "",
            requestTypeName: "",
            requestTypeDefinitionId: "",
            sections: {},
            amount: null,
            paymentMethod: "pay_now",
            payOnSubmit: null,
        },
        mode: "onTouched",
    });

    const sectionDefinitionsRef = useRef<SectionDefinition[]>([]);
    const [forceValidateSections, setForceValidateSections] = useState(false);

    const setSectionDefinitions = (defs: SectionDefinition[]) => {
        sectionDefinitionsRef.current = defs;
    };

    const validateStep = async (stepIndex: number): Promise<boolean> => {
        const fields = stepFields[stepIndex];
        const rhfValid = fields ? await form.trigger(fields) : true;

        if (stepIndex === 1) {
            const storedSections = form.getValues("sections");
            const allSectionsValid = sectionDefinitionsRef.current.every((section) =>
                isSectionValid(section, storedSections?.[section.id!] ?? [])
            );
            if (!allSectionsValid) setForceValidateSections(true);
            return rhfValid && allSectionsValid;
        }

        return rhfValid;
    };

    return (
        <ServiceRequestFormContext.Provider value={{ form, validateStep, setSectionDefinitions, forceValidateSections, setForceValidateSections }}>
            {children}
        </ServiceRequestFormContext.Provider>
    );
};

export const useServiceRequestForm = () => {
    const ctx = useContext(ServiceRequestFormContext);
    if (!ctx) throw new Error("useServiceRequestForm must be used inside ServiceRequestFormProvider");
    return ctx;
};
